import React from "react";

/**
 * FIFA-style GPS Card. Pure visual; no API calls. Exported to PNG via html-to-image.
 * Fixed pixel dimensions 480x720 — wrapper in MyGPS handles responsive scale-to-fit.
 */
const PILLAR_KEYS = [
  { k: "strength", label: "STR", letter: "S" },
  { k: "speed", label: "SPD", letter: "F" },
  { k: "explosive", label: "EXP", letter: "E" },
  { k: "stamina", label: "STA", letter: "P" },
  { k: "control", label: "CTL", letter: "C" },
];

const GPS_LOGO = "https://customer-assets.emergentagent.com/job_gps-arena-1/artifacts/itknedvj_GPS%20Logo%20AI-09.png";
const GLADE_LOGO = "https://customer-assets.emergentagent.com/job_gps-arena-1/artifacts/ruktsz3k_Glade%20Logo%202024%20zoomed%20in%20white%20empty.png";

export default function PlayerCard({
  name = "ATHLETE",
  avatarUrl = null,
  date = new Date(),
  gps = 0,
  archetype = null,
  pillars = {},
  country = "",
  gym = "",
  verified = false,
  publicId = "",
}) {
  const dateStr = (date instanceof Date ? date : new Date(date)).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
  }).toUpperCase();

  // Radar polygon
  const cx = 50, cy = 54, R = 36;
  const pts = PILLAR_KEYS.map((p, i) => {
    const v = Math.max(0, Math.min(100, Number(pillars[p.k] || 0))) / 100;
    const ang = -Math.PI / 2 + (i * 2 * Math.PI) / PILLAR_KEYS.length;
    const x = cx + Math.cos(ang) * R * v;
    const y = cy + Math.sin(ang) * R * v;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");

  const axisPts = PILLAR_KEYS.map((_, i) => {
    const ang = -Math.PI / 2 + (i * 2 * Math.PI) / PILLAR_KEYS.length;
    return { x: cx + Math.cos(ang) * R, y: cy + Math.sin(ang) * R };
  });

  return (
    <div
      data-testid="player-card"
      style={{
        width: 540,
        height: 675,
        background: "linear-gradient(180deg, #0a0e16 0%, #050709 100%)",
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 18,
        position: "relative",
        overflow: "hidden",
        color: "#fff",
        fontFamily: "'AERO', 'JetBrains Mono', monospace",
      }}
    >
      {/* Diagonal glow stripe */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.08), transparent 55%), radial-gradient(circle at 80% 100%, rgba(0,240,255,0.06), transparent 60%)",
        pointerEvents: "none",
      }} />

      {/* Corner ticks */}
      {[
        { id: "tl", top: 14, left: 14 }, { id: "tr", top: 14, right: 14 },
        { id: "bl", bottom: 14, left: 14 }, { id: "br", bottom: 14, right: 14 },
      ].map(({ id, ...s }, i) => (
        <div key={id} style={{
          position: "absolute", ...s,
          width: 14, height: 14,
          borderTop: i < 2 ? "1px solid rgba(255,255,255,0.55)" : "none",
          borderBottom: i >= 2 ? "1px solid rgba(255,255,255,0.55)" : "none",
          borderLeft: (i % 2 === 0) ? "1px solid rgba(255,255,255,0.55)" : "none",
          borderRight: (i % 2 === 1) ? "1px solid rgba(255,255,255,0.55)" : "none",
        }} />
      ))}

      {/* Top header */}
      <div style={{
        position: "absolute", top: 26, left: 0, right: 0,
        display: "flex", justifyContent: "space-between",
        padding: "0 32px",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10, letterSpacing: "0.32em",
        color: "rgba(255,255,255,0.7)",
      }}>
        <span>GPS · GENERAL PHYSICALITY</span>
        <span>{dateStr}</span>
      </div>

      {/* Public ID — unique 10-char identifier, prominent under header */}
      {publicId && (
        <div style={{
          position: "absolute", top: 46, left: 32,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, letterSpacing: "0.4em",
          color: "rgba(0,240,255,0.9)",
        }}>
          ID · {publicId}
        </div>
      )}

      {/* GPS big number */}
      <div style={{
        position: "absolute", top: 60, left: 32,
        display: "flex", flexDirection: "column", alignItems: "flex-start",
      }}>
        <div style={{ fontSize: 96, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.02em" }}>
          {Number(gps || 0).toFixed(1)}
        </div>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, letterSpacing: "0.3em",
          color: "rgba(255,255,255,0.55)", marginTop: 4,
        }}>
          OVERALL · / 100
        </div>
      </div>

      {/* Archetype code badge */}
      <div style={{
        position: "absolute", top: 64, right: 32,
        textAlign: "right",
      }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 14, letterSpacing: "0.4em",
          color: "rgba(255,255,255,0.9)",
        }}>
          {archetype?.code || "?????"}
        </div>
        <div style={{ fontSize: 22, marginTop: 4, fontWeight: 800, letterSpacing: "0.02em" }}>
          {(archetype?.name || "—").toUpperCase()}
        </div>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9, letterSpacing: "0.3em",
          color: "rgba(0,240,255,0.85)", marginTop: 2,
        }}>
          {(archetype?.tag || "").toUpperCase()}
        </div>
      </div>

      {/* Avatar */}
      <div style={{
        position: "absolute", top: 180, left: "50%", transform: "translateX(-50%)",
        width: 150, height: 150, borderRadius: 75,
        border: "2px solid rgba(255,255,255,0.45)",
        background: avatarUrl ? `url(${avatarUrl}) center/cover` : "linear-gradient(135deg, #1a2030, #0a0e16)",
        boxShadow: "0 0 32px rgba(0,240,255,0.18)",
      }}>
        {!avatarUrl && (
          <div style={{
            width: "100%", height: "100%", display: "grid", placeItems: "center",
            fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
            letterSpacing: "0.3em", color: "rgba(255,255,255,0.4)",
          }}>NO PHOTO</div>
        )}
      </div>

      {/* Name */}
      <div style={{
        position: "absolute", top: 340, left: 0, right: 0,
        textAlign: "center", padding: "0 32px",
      }}>
        <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: "0.04em", lineHeight: 1.1 }}>
          {(name || "ATHLETE").toUpperCase()}
        </div>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, letterSpacing: "0.3em",
          color: "rgba(255,255,255,0.5)", marginTop: 6,
        }}>
          {country ? country.toUpperCase() : "GLOBAL"}{gym ? ` · ${gym.toUpperCase()}` : ""}
        </div>
      </div>

      {/* Pillars row */}
      <div style={{
        position: "absolute", top: 398, left: 24, right: 24,
        display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6,
      }}>
        {PILLAR_KEYS.map((p) => {
          const v = Number(pillars[p.k] || 0);
          return (
            <div key={p.k} style={{
              border: "1px solid rgba(255,255,255,0.18)", borderRadius: 8,
              padding: "10px 6px", textAlign: "center",
              background: "rgba(255,255,255,0.03)",
            }}>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9, letterSpacing: "0.22em",
                color: "rgba(255,255,255,0.5)",
              }}>
                {p.label} <span style={{ color: "rgba(0,240,255,0.9)" }}>{p.letter}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>
                {v.toFixed(0)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Radar — centered, shifted up to make room for logo footer */}
      <div style={{
        position: "absolute", bottom: 64, left: "50%", transform: "translateX(-50%)",
        width: 150, height: 150,
      }}>
        <svg viewBox="0 0 100 100" width="100%" height="100%">
          {[1, 0.75, 0.5, 0.25].map((s) => (
            <polygon key={`ring-${s}`}
              points={axisPts.map((p) => `${cx + (p.x - cx) * s},${cy + (p.y - cy) * s}`).join(" ")}
              fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.4"
            />
          ))}
          {axisPts.map((p, i) => (
            <line key={`axis-${PILLAR_KEYS[i].k}`} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" />
          ))}
          <polygon points={pts} fill="rgba(0,240,255,0.18)" stroke="rgba(0,240,255,0.95)" strokeWidth="0.8" />
          {axisPts.map((p, i) => (
            <text key={`label-${PILLAR_KEYS[i].k}`}
              x={p.x + (p.x - cx) * 0.18}
              y={p.y + (p.y - cy) * 0.18 + 1.5}
              textAnchor="middle"
              fontSize="4.2"
              fill="rgba(255,255,255,0.7)"
              fontFamily="'JetBrains Mono', monospace"
              style={{ letterSpacing: "0.15em" }}
            >
              {PILLAR_KEYS[i].letter}
            </text>
          ))}
        </svg>
      </div>

      {/* Logo footer — GPS (left) · Verified badge (center) · Glade (right) */}
      <div style={{
        position: "absolute", bottom: 22, left: 28, right: 28,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <img
          src={GPS_LOGO}
          alt="GPS"
          crossOrigin="anonymous"
          style={{ width: 34, height: 34, objectFit: "contain", opacity: 0.9 }}
        />
        {verified ? (
          <div
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "5px 12px",
              border: "1px solid rgba(0,240,255,0.7)",
              borderRadius: 999,
              background: "rgba(0,240,255,0.08)",
              boxShadow: "0 0 12px rgba(0,240,255,0.25)",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, letterSpacing: "0.32em",
              color: "#00f0ff",
              textTransform: "uppercase",
            }}
          >
            <span
              style={{
                width: 6, height: 6, borderRadius: 999,
                background: "#00f0ff",
                boxShadow: "0 0 6px #00f0ff",
              }}
            />
            VERIFIED
          </div>
        ) : (
          <div style={{ height: 24 }} />
        )}
        <img
          src={GLADE_LOGO}
          alt="Glade"
          crossOrigin="anonymous"
          style={{ width: 34, height: 34, objectFit: "contain", opacity: 0.9 }}
        />
      </div>
    </div>
  );
}
